import * as _ from 'lodash';
import { overlayColors } from 'common/constants';
import { applyPointFilter, hasActiveFilter, mergeWithFilteredPoints } from './overlayPointFilter';

export { hasActiveFilter, mergeWithFilteredPoints };

export function areTimestampsEqual(ts1, ts2) {
  if (!ts1 || !ts2) return false;

  const date1 = ts1 instanceof Date ? ts1 : new Date(ts1);
  const date2 = ts2 instanceof Date ? ts2 : new Date(ts2);

  // Reject Invalid Dates — two would otherwise compare equal (both NaN time).
  if (Number.isNaN(date1.getTime()) || Number.isNaN(date2.getTime())) return false;

  return _.isEqual(date1, date2);
}

/**
 * Validates and repairs marker metadata for overlay points
 * @param {Array} points - Array of overlay points to validate
 * @returns {Array} - Array of validated/repaired points
 */
export function validateAndRepairMarkerMetadata(points) {
  if (!points || !Array.isArray(points)) return points;

  return points.map((point, pointIndex) => {
    if (!point.spectrumFeatures || !point.spectrumFeatures.items) {
      return point;
    }

    const repairedFeatures = {
      ...point.spectrumFeatures,
      items: point.spectrumFeatures.items.map((feature, featureIndex) => {
        const repairedFeature = { ...feature };

        if (feature.isOverlayMarker || point.overlayColor) {
          if (!repairedFeature.pointIndex && repairedFeature.pointIndex !== 0) {
            repairedFeature.pointIndex = pointIndex;
          }
          if (!repairedFeature.originalPointIndex && repairedFeature.originalPointIndex !== 0) {
            repairedFeature.originalPointIndex = point.originalIndex || pointIndex;
          }

          if (point.overlayColor && !repairedFeature.overlayColor) {
            repairedFeature.overlayColor = point.overlayColor;
          }

          if (point.timestamp && !repairedFeature.timestamp) {
            repairedFeature.timestamp = point.timestamp;
          }
        }

        if (typeof repairedFeature.frequency !== 'number' || !Number.isFinite(repairedFeature.frequency)) {
          console.warn(`Invalid marker frequency for feature ${featureIndex} in point ${pointIndex}:`, repairedFeature);
        }

        return repairedFeature;
      })
    };

    return {
      ...point,
      spectrumFeatures: repairedFeatures
    };
  });
}

/**
 * Determines the appropriate X-axis unit for spectrum display
 */
export function determineXAxisUnit(selection, config, showOverlay, spectrumXUnit) {
  if (!config || typeof showOverlay !== 'boolean' || typeof spectrumXUnit !== 'string') {
    return 'Hz';
  }

  if (selection && typeof selection.preserveXUnit === 'string') {
    return selection.preserveXUnit;
  }

  if (showOverlay && spectrumXUnit === 'Orders') {
    return 'Orders';
  }

  return (config && config.spectrum && config.spectrum.freq_units) ?
    config.spectrum.freq_units : 'Hz';
}

export function assignPointColors(points, filterCriteria) {
  // Preserve a visible point's already-baked color, then give each new (colorless)
  // visible point the first palette color not already in use. Assigning by position
  // would collide with an existing point colored from a higher palette index (e.g.
  // remove the middle of three points, add one, and it reuses the third point's color
  // instead of the freed-up second color).
  const usedColors = new Set();
  points.forEach((point) => {
    if (point.color && applyPointFilter(point, filterCriteria)) {
      usedColors.add(point.color);
    }
  });

  let fallbackIndex = 0;
  const takeNextColor = () => {
    const available = overlayColors.find(color => !usedColors.has(color));
    if (available !== undefined) {
      usedColors.add(available);
      return available;
    }
    // Every palette color is in use — cycle deterministically as a last resort.
    const color = overlayColors[fallbackIndex % overlayColors.length];
    fallbackIndex += 1;
    return color;
  };

  return points.map((point) => {
    if (!applyPointFilter(point, filterCriteria)) {
      return { ...point };
    }
    const color = point.color ? point.color : takeNextColor();
    return { ...point, color };
  });
}

/**
 * Computes state update for selected points after color assignment
 */
export function getPointsStateUpdate(pointsWithColors, showOverlay, spectrumXUnit, shaftSpeed) {
  // !spectrumXUnit (not !== 'Orders') so a deliberate Hz/CPM choice isn't overwritten.
  if (pointsWithColors.length > 0 && showOverlay && !spectrumXUnit &&
    shaftSpeed && shaftSpeed > 0.001) {
    return {
      selectedPoints: pointsWithColors,
      spectrumXUnit: 'Orders'
    };
  }
  return { selectedPoints: pointsWithColors };
}

/**
 * Merges, limits, and colors overlay points for state update
 */
export function prepareSelectedPointsUpdate(newPoints, currentPoints, filterCriteria) {
  const filterActive = hasActiveFilter(filterCriteria);

  const finalPoints = mergeWithFilteredPoints(
    newPoints,
    currentPoints,
    filterCriteria,
    filterActive
  );

  const pointsWithColors = assignPointColors(finalPoints, filterCriteria);

  return { finalPoints, pointsWithColors };
}
